import { ArrowDownTrayIcon, DocumentIcon, LanguageIcon } from '@heroicons/react/24/outline';
import { Button, ButtonProps, Group, Loader, Modal, Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { ExportService } from '../../../services/exportService';
import { EuropassService } from '../../../services/europassService';
import euFlag from '../../../images/Flag_of_Europe.png';

export type ExportLanguage = 'None' | 'English' | 'Polish' | 'French' | 'Norwegian';
export type AnonymizationOption = 'Default' | 'WithoutNames';

interface CVDownloadMenuProps {
  personId: number;
  userName?: string;
  exportLoading?: string | null;
  onDownload?: (format: 'word' | 'pdf', language: ExportLanguage, anonymization: AnonymizationOption) => void;
  buttonProps?: ButtonProps;
  fullWidth?: boolean;
}

const LANGUAGE_OPTIONS: { value: ExportLanguage; label: string }[] = [
  { value: 'None', label: 'Original' },
  { value: 'English', label: 'English' },
  { value: 'Polish', label: 'Polish' },
  { value: 'French', label: 'French' },
  { value: 'Norwegian', label: 'Norwegian' }
];

const ANONYMIZATION_OPTIONS: { value: AnonymizationOption; label: string }[] = [
  { value: 'Default', label: 'With Names' },
  { value: 'WithoutNames', label: 'Without Names' }
];

export function CVDownloadMenu({
  personId,
  userName = '',
  exportLoading,
  onDownload,
  buttonProps = {},
  fullWidth = false
}: CVDownloadMenuProps) {
  const [opened, setOpened] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<ExportLanguage>('None');
  const [selectedAnonymization, setSelectedAnonymization] = useState<AnonymizationOption>('Default');
  const [localExportLoading, setLocalExportLoading] = useState<'word' | 'pdf' | 'europass' | null>(null);
  const exportService = new ExportService();
  const europassService = new EuropassService();

  const currentExportLoading = exportLoading !== undefined ? exportLoading : localExportLoading;

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  };

  const handleDownloadWord = async () => {
    // If external onDownload function is provided, use it
    if (onDownload) {
      try {
        onDownload('word', selectedLanguage, selectedAnonymization);
        setOpened(false);
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        notifications.show({
          title: 'Download Failed',
          message: `Failed to download CV as Word document: ${errorMessage}`,
          color: 'red',
          autoClose: 5000
        });
      }
      return;
    }

    // Otherwise, handle download internally
    try {
      setLocalExportLoading('word');
      const blob = await exportService.exportToWord(personId, selectedLanguage, selectedAnonymization);
      
      if (!blob || blob.size === 0) {
        throw new Error('Received empty file from server');
      }
      
      downloadFile(blob, `CV_${userName.replace(/\s+/g, '_') || personId}.docx`);
      setOpened(false);
      
      notifications.show({
        title: 'Success',
        message: 'CV downloaded successfully as Word document',
        color: 'green',
        autoClose: 3000
      });
    } catch (error) {
      console.error('Error downloading CV as Word:', error);
      const errorMessage = getErrorMessage(error);
      notifications.show({
        title: 'Download Failed',
        message: `Failed to download CV as Word document. ${errorMessage}`,
        color: 'red',
        autoClose: 5000
      });
    } finally {
      setLocalExportLoading(null);
    }
  };

  const handleDownloadPdf = async () => {
    // If external onDownload function is provided, use it
    if (onDownload) {
      try {
        onDownload('pdf', selectedLanguage, selectedAnonymization);
        setOpened(false);
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        notifications.show({
          title: 'Download Failed',
          message: `Failed to download CV as PDF document: ${errorMessage}`,
          color: 'red',
          autoClose: 5000
        });
      }
      return;
    }

    // Otherwise, handle download internally
    try {
      setLocalExportLoading('pdf');
      const blob = await exportService.exportToPdf(personId, selectedLanguage, selectedAnonymization);
      
      if (!blob || blob.size === 0) {
        throw new Error('Received empty file from server');
      }
      
      downloadFile(blob, `CV_${userName.replace(/\s+/g, '_') || personId}.pdf`);
      setOpened(false);
      
      notifications.show({
        title: 'Success',
        message: 'CV downloaded successfully as PDF document',
        color: 'green',
        autoClose: 3000
      });
    } catch (error) {
      console.error('Error downloading CV as PDF:', error);
      const errorMessage = getErrorMessage(error);
      notifications.show({
        title: 'Download Failed',
        message: `Failed to download CV as PDF document. ${errorMessage}`,
        color: 'red',
        autoClose: 5000
      });
    } finally {
      setLocalExportLoading(null);
    }
  };

  const handleDownloadEuropassPdf = async () => {
    try {
      setLocalExportLoading('europass');
      const blob = await europassService.exportToPdf(personId, selectedLanguage);
      
      if (!blob || blob.size === 0) {
        throw new Error('Received empty file from server');
      }
      
      downloadFile(blob, `Europass_CV_${userName.replace(/\s+/g, '_') || personId}.pdf`);
      setOpened(false);
      
      notifications.show({
        title: 'Success',
        message: 'CV downloaded successfully as Europass PDF',
        color: 'green',
        autoClose: 3000
      });
    } catch (error) {
      console.error('Error downloading CV as Europass PDF:', error);
      const errorMessage = getErrorMessage(error);
      notifications.show({
        title: 'Download Failed',
        message: `Failed to download CV as Europass PDF. ${errorMessage}`,
        color: 'red',
        autoClose: 5000
      });
    } finally {
      setLocalExportLoading(null);
    }
  };

  // Helper function to trigger file download
  const downloadFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <Button
        variant="light"
        color="brand"
        size="sm"
        ml={!fullWidth ? "auto" : undefined}
        leftSection={currentExportLoading ? <Loader size="xs" /> : <ArrowDownTrayIcon width={18} />}
        loading={!!currentExportLoading}
        fullWidth={fullWidth}
        onClick={() => setOpened(true)}
        {...buttonProps}
      >
        Download
      </Button>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title="Download CV"
        size="md"
        centered
      >
        <Stack gap="lg">
          {/* Language Selection */}
          <div>
            <Group gap="xs" mb="sm">
              <LanguageIcon width={20} className="text-gray-700" />
              <Text fw={500} size="sm">Translation Language</Text>
            </Group>
            <Select
              data={LANGUAGE_OPTIONS}
              value={selectedLanguage}
              onChange={(value) => setSelectedLanguage(value as ExportLanguage)}
              disabled={!!currentExportLoading}
              placeholder="Select language"
            />
          </div>

          {/* Anonymization Selection */}
          <div>
            <Text fw={500} size="sm" mb="sm">Anonymization</Text>
            <Select
              data={ANONYMIZATION_OPTIONS}
              value={selectedAnonymization}
              onChange={(value) => setSelectedAnonymization(value as AnonymizationOption)}
              disabled={!!currentExportLoading}
              placeholder="Select anonymization option"
            />
          </div>

          {/* Export Buttons */}
          <Group grow>
            <Button
              variant="light"
              leftSection={<DocumentIcon width={18} />}
              rightSection={currentExportLoading === 'word' && <Loader size="xs" />}
              onClick={handleDownloadWord}
              disabled={!!currentExportLoading}
              loading={currentExportLoading === 'word'}
            >
              Word (.docx)
            </Button>
            <Button
              variant="light"
              leftSection={<DocumentIcon width={18} />}
              rightSection={currentExportLoading === 'pdf' && <Loader size="xs" />}
              onClick={handleDownloadPdf}
              disabled={!!currentExportLoading}
              loading={currentExportLoading === 'pdf'}
            >
              PDF (.pdf)
            </Button>
          </Group>

          {/* Europass PDF Button */}
          <Group justify="center">
            <Button
              variant="light"
              leftSection={<img src={euFlag} alt="EU Flag" style={{ height: 18, width: 'auto', objectFit: 'contain' }} />}
              rightSection={currentExportLoading === 'europass' && <Loader size="xs" />}
              onClick={handleDownloadEuropassPdf}
              disabled={!!currentExportLoading}
              loading={currentExportLoading === 'europass'}
            >
              EUROPASS PDF
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
